import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import apiClient from "../Api/apiConfig";
import UserGrievancesPage from "./UserGrievancesPage";
import "../styles/ProfilePage.css";

// ✅ Schéma de validation pour le profil
const profileSchema = z.object({
  firstName: z
    .string()
    .min(2, "Le prénom doit contenir au moins 2 caractères"),
  lastName: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  email: z.string().email("Email invalide"),
  phone: z
    .string()
    .regex(/^\+?[0-9\s.-]{10,15}$/, "Numéro de téléphone invalide")
    .or(z.literal(""))
    .optional(),
  address: z
    .string()
    .min(10, "Adresse trop courte")
    .or(z.literal(""))
    .optional(),
});

// ✅ Schéma de validation pour le changement de mot de passe
const passwordSchema = z
  .object({
    currentPassword: z.string().min(6, "Le mot de passe actuel est requis"),
    newPassword: z
      .string()
      .min(6, "Le nouveau mot de passe doit contenir au moins 6 caractères"),
    confirmPassword: z
      .string()
      .min(6, "Vous devez confirmer le mot de passe"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Les mots de passe ne correspondent pas",
  });

// Récupérer le token et l'ID utilisateur
const getAuthToken = () =>
  localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

const getUserIdFromStorage = () => {
  const user = localStorage.getItem("user") || sessionStorage.getItem("user");
  return user ? JSON.parse(user).id : null;
};

// 🔗 Base URL backend (pour les fichiers /uploads)
const API_BASE_URL = import.meta.env.VITE_API_URL || "";
// si VITE_API_URL = "http://localhost:5000/api" → FILE_BASE_URL = "http://localhost:5000"
const FILE_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, "");

// utilitaire pour construire une URL d’avatar correcte
const buildAvatarUrl = (value) => {
  if (!value) return null;
  if (value.startsWith("http") || value.startsWith("blob:")) return value;
  return `${FILE_BASE_URL}${value}`;
};

export default function ProfilePage() {
  const [activeTab, setActiveTab] = useState("profile"); // Onglet actif
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [profileData, setProfileData] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const authToken = getAuthToken();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(profileSchema),
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm({
    resolver: zodResolver(passwordSchema),
  });

  // Charger les données utilisateur
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userId = getUserIdFromStorage();
        if (!userId || !authToken) {
          setError("Vous devez être connecté pour accéder à cette page.");
          setLoading(false);
          return;
        }

        const { data } = await apiClient.get(`/users/${userId}`);

        setProfileData(data);

        // Pré-remplir les champs du formulaire (mapping snake_case → camelCase)
        setValue("firstName", data.first_name || "");
        setValue("lastName", data.last_name || "");
        setValue("email", data.email || "");
        setValue("phone", data.phone || "");
        setValue("address", data.address || "");

        // Avatar : on supporte avatar_url OU avatarUrl
        if (data.avatar_url || data.avatarUrl) {
          setAvatarPreview(
            buildAvatarUrl(data.avatar_url || data.avatarUrl)
          );
        }
      } catch (err) {
        console.error("Erreur chargement profil :", err);
        setError("Impossible de charger les données du profil.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [authToken, setValue]);

  // Soumission du formulaire pour le profil
  const onSubmitProfile = async (formValues) => {
    setError("");
    setSuccessMessage("");
    setSavingProfile(true);

    try {
      const userId = getUserIdFromStorage();
      if (!userId) {
        setError("Impossible de trouver votre identifiant utilisateur.");
        setSavingProfile(false);
        return;
      }

      // Mapping camelCase → snake_case pour le backend
      const payload = {
        first_name: formValues.firstName,
        last_name: formValues.lastName,
        email: formValues.email,
        phone: formValues.phone || null,
        address: formValues.address || null,
      };

      await apiClient.put(`/users/${userId}`, payload);

      setSuccessMessage("Profil mis à jour avec succès.");
      setProfileData((prev) =>
        prev
          ? {
              ...prev,
              ...payload,
            }
          : prev
      );
    } catch (err) {
      console.error("Erreur mise à jour profil :", err);
      setError(
        err.response?.data?.message ||
          "Une erreur est survenue lors de la mise à jour du profil."
      );
    } finally {
      setSavingProfile(false);
    }
  };

  // Soumission du formulaire pour le mot de passe
  const onSubmitPassword = async (values) => {
    setError("");
    setSuccessMessage("");
    setSavingPassword(true);

    try {
      await apiClient.post("/users/change-password", {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      setSuccessMessage("Mot de passe mis à jour avec succès.");
      resetPasswordForm();
    } catch (err) {
      console.error("Erreur mise à jour mot de passe :", err);
      setError(
        err.response?.data?.message ||
          "Une erreur est survenue lors de la mise à jour du mot de passe."
      );
    } finally {
      setSavingPassword(false);
    }
  };

  // ✅ Upload / changement de photo de profil
  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError("");
    setSuccessMessage("");

    // Petit contrôle basique
    if (!file.type.startsWith("image/")) {
      setError("Merci de sélectionner une image valide (jpg, png, etc.).");
      return;
    }

    const userId = getUserIdFromStorage();
    if (!userId) {
      setError("Impossible de trouver votre identifiant utilisateur.");
      return;
    }

    // Pré-aperçu local instantané
    const localPreview = URL.createObjectURL(file);
    setAvatarPreview(localPreview);

    setUploadingAvatar(true);

    try {
      const formData = new FormData();
      formData.append("avatar", file);

      const { data } = await apiClient.post(
        `/users/${userId}/avatar`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const newAvatarPath = data.avatarUrl || data.avatar_url || null;
      const finalUrl = buildAvatarUrl(newAvatarPath) || localPreview;

      setAvatarPreview(finalUrl);
      setProfileData((prev) =>
        prev
          ? {
              ...prev,
              avatar_url: newAvatarPath,
            }
          : prev
      );

      setSuccessMessage("Photo de profil mise à jour avec succès.");
    } catch (err) {
      console.error("Erreur lors du téléchargement de l'avatar :", err);
      setError(
        err.response?.data?.message ||
          "Une erreur est survenue lors du changement de la photo de profil."
      );
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-header">
          <h1>Mon Profil</h1>
        </div>
        <p>Chargement des informations...</p>
      </div>
    );
  }

  if (error && !profileData) {
    return (
      <div className="profile-page">
        <div className="profile-header">
          <h1>Mon Profil</h1>
        </div>
        <div className="error-box">{error}</div>
      </div>
    );
  }

  const avatarSrc =
    avatarPreview ||
    buildAvatarUrl(profileData?.avatar_url || profileData?.avatarUrl) ||
    "https://placehold.co/100x100?text=User";

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>Mon Profil</h1>
        <p>Gérez vos informations personnelles et vos accès</p>
      </div>

      <div className="profile-container">
        {/* COLONNE GAUCHE : CARTE USER */}
        <div className="profile-sidebar">
          <div className="profile-card">
            <div className="avatar-wrapper">
              <img src={avatarSrc} alt="Avatar" className="profile-avatar" />
              <label className="avatar-upload-label">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="avatar-upload-input"
                />
                {uploadingAvatar ? "Mise à jour..." : "Changer la photo"}
              </label>
            </div>

            <h3>
              {profileData?.first_name} {profileData?.last_name}
            </h3>
            <p className="profile-role">
              {profileData?.role === "admin"
                ? "ADMINISTRATEUR"
                : profileData?.role === "member"
                ? "MEMBRE"
                : "UTILISATEUR"}
            </p>
            <p className="profile-email">{profileData?.email}</p>

            {profileData?.address && (
              <p className="profile-address">
                <strong>Adresse :</strong> {profileData.address}
              </p>
            )}
            {profileData?.phone && (
              <p className="profile-phone">
                <strong>Téléphone :</strong> {profileData.phone}
              </p>
            )}
          </div>
        </div>

        {/* COLONNE DROITE : CONTENU / ONGLET */}
        <div className="profile-content">
          {/* Onglets */}
          <div className="tabs">
            <button
              className={`tab-button ${
                activeTab === "profile" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("profile");
                setError("");
                setSuccessMessage("");
              }}
            >
              Profil
            </button>
            <button
              className={`tab-button ${
                activeTab === "password" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("password");
                setError("");
                setSuccessMessage("");
              }}
            >
              Changer le mot de passe
            </button>
            <button
              className={`tab-button ${
                activeTab === "grievances" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("grievances");
                setError("");
                setSuccessMessage("");
              }}
            >
              Mes doléances
            </button>
          </div>

          {/* Messages globaux */}
          {error && <div className="error-box">{error}</div>}
          {successMessage && (
            <div className="success-box">{successMessage}</div>
          )}

          {/* Contenu des onglets */}
          <div className="tab-content">
            {/* 🧍 Onglet profil */}
            {activeTab === "profile" && (
              <form
                className="profile-form"
                onSubmit={handleSubmit(onSubmitProfile)}
              >
                <div className="form-row">
                  <div className="form-group">
                    <label>Prénom</label>
                    <input
                      type="text"
                      {...register("firstName")}
                      className={errors.firstName ? "input-error" : ""}
                    />
                    {errors.firstName && (
                      <p className="error-text">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Nom</label>
                    <input
                      type="text"
                      {...register("lastName")}
                      className={errors.lastName ? "input-error" : ""}
                    />
                    {errors.lastName && (
                      <p className="error-text">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    {...register("email")}
                    className={errors.email ? "input-error" : ""}
                  />
                  {errors.email && (
                    <p className="error-text">{errors.email.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label>Téléphone</label>
                  <input
                    type="text"
                    {...register("phone")}
                    placeholder="Ex : 06 12 34 56 78"
                    className={errors.phone ? "input-error" : ""}
                  />
                  {errors.phone && (
                    <p className="error-text">{errors.phone.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label>Adresse</label>
                  <textarea
                    rows={3}
                    {...register("address")}
                    className={errors.address ? "input-error" : ""}
                  />
                  {errors.address && (
                    <p className="error-text">{errors.address.message}</p>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={savingProfile}
                  >
                    {savingProfile
                      ? "Enregistrement..."
                      : "Enregistrer les modifications"}
                  </button>
                </div>
              </form>
            )}

            {/* 🔑 Onglet mot de passe */}
            {activeTab === "password" && (
              <form
                className="profile-form"
                onSubmit={handlePasswordSubmit(onSubmitPassword)}
              >
                <div className="form-group">
                  <label>Mot de passe actuel</label>
                  <input
                    type="password"
                    {...registerPassword("currentPassword")}
                    className={
                      passwordErrors.currentPassword ? "input-error" : ""
                    }
                  />
                  {passwordErrors.currentPassword && (
                    <p className="error-text">
                      {passwordErrors.currentPassword.message}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label>Nouveau mot de passe</label>
                  <input
                    type="password"
                    {...registerPassword("newPassword")}
                    className={
                      passwordErrors.newPassword ? "input-error" : ""
                    }
                  />
                  {passwordErrors.newPassword && (
                    <p className="error-text">
                      {passwordErrors.newPassword.message}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label>Confirmer le nouveau mot de passe</label>
                  <input
                    type="password"
                    {...registerPassword("confirmPassword")}
                    className={
                      passwordErrors.confirmPassword ? "input-error" : ""
                    }
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="error-text">
                      {passwordErrors.confirmPassword.message}
                    </p>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={savingPassword}
                  >
                    {savingPassword
                      ? "Mise à jour..."
                      : "Mettre à jour le mot de passe"}
                  </button>
                </div>
              </form>
            )}

            {/* 📂 Onglet Mes doléances */}
            {activeTab === "grievances" && <UserGrievancesPage />}
          </div>
        </div>
      </div>
    </div>
  );
}
